import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import { Controller, useForm } from "react-hook-form";
import { useLocalUserState } from "../util/useLocalUserState.ts";
import { User } from "@labs/shared/types/User.ts";
import { callEmailLambda } from "../util/callEmailLambda.ts";
import { useLocalSolutionMapState } from "../util/useLocalSolutionMapState.ts";
import { useLocalTargetState } from "../util/useLocalTargetState.ts";
import { useMemo, useState } from "react";
import { analyzePlan } from "../util/planAnalysis/analyzePlan.ts";
import { useLocalBaselineEmissionsState } from "../util/useLocalBaselineEmissionsState.ts";
import { trackEvent } from "../util/analytics.ts";
import { EventCategory } from "@labs/shared/types/EventCategory.ts";
import { useStartOver } from "../util/useStartOver.ts";
import { ErrorOutline } from "@mui/icons-material";
import { MarketingOptInLabel } from "../components/MarketingOptInLabel.tsx";
import { green } from "@mui/material/colors";

enum FormStatus {
  Success = "success",
  Error = "error",
  Loading = "loading",
  Idle = "idle",
}

const EmailPlanForm = () => {
  const [user, setUser] = useLocalUserState();
  const [solutionMap] = useLocalSolutionMapState();
  const [target] = useLocalTargetState();
  const [baselineEmissions] = useLocalBaselineEmissionsState();
  const analyzedPlan = useMemo(
    () => analyzePlan(solutionMap, target, baselineEmissions),
    [baselineEmissions, solutionMap, target],
  );
  const [formStatus, setFormStatus] = useState(FormStatus.Idle);

  // note: this local state var tracks the current state of this send, whereas
  // organization.planEmailed tracks whether this user has ever sent a plan
  const { handleStartOver } = useStartOver();
  const { control, handleSubmit } = useForm<User>({
    mode: "onChange",
    defaultValues: {
      ...user,
      name: user.name || "",
      email: user.email || "",
      jobTitle: user.jobTitle || "",
      organizationName: user.organizationName || "",
    },
  });

  const sendPlan = (data: User) => {
    setFormStatus(FormStatus.Loading);

    const newUser = { ...user, ...data, planEmailed: true };
    setUser(newUser);

    trackEvent(EventCategory.Plan, "Email Plan", "Submit Form");

    callEmailLambda(
      newUser,
      baselineEmissions,
      solutionMap,
      target,
      analyzedPlan,
    )
      .then(() => {
        setFormStatus(FormStatus.Success);
      })
      .catch(() => {
        setFormStatus(FormStatus.Error);
      });
  };

  const formFields = (
    <>
      <Controller
        name="name"
        control={control}
        render={({ field }) => <TextField id="name" label="Name" {...field} />}
      />
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <TextField id="email" type="email" label="Email Address" {...field} />
        )}
      />
      <Controller
        name="jobTitle"
        control={control}
        render={({ field }) => (
          <TextField id="jobTitle" label="Title (optional)" {...field} />
        )}
      />
      <Controller
        name="organizationName"
        control={control}
        render={({ field }) => (
          <TextField
            id="organizationName"
            label="Company (optional)"
            {...field}
          />
        )}
      />
      <MarketingOptInLabel />
    </>
  );

  return (
    <form onSubmit={handleSubmit(sendPlan)}>
      {formStatus === FormStatus.Error && (
        <Stack direction={"column"} spacing={3}>
          <Typography variant={"h6"} color={"error.main"} align={"center"}>
            We're Sorry
          </Typography>
          <ErrorOutline
            color={"error"}
            fontSize={"large"}
            sx={{
              alignSelf: "center",
            }}
          />
          <Typography variant={"body2"} align={"center"}>
            There was an error sending your plan. Try again later.
          </Typography>
          {formFields}
          <Button variant="outlined" color="primary" type="submit">
            Resend
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={handleStartOver}
          >
            Start Over
          </Button>
        </Stack>
      )}
      {formStatus === FormStatus.Loading && (
        <Stack direction={"column"} spacing={3}>
          <Typography variant={"h6"} color={"info.main"} align={"center"}>
            Loading...
          </Typography>
          <CircularProgress color={"info"} sx={{ alignSelf: "center" }} />
          <Typography variant={"body2"} align={"center"}>
            Your customized plan is being generated. Please wait...
          </Typography>
        </Stack>
      )}
      {formStatus === FormStatus.Success && (
        <Stack direction={"column"} spacing={3}>
          <Typography variant={"h6"} color={"success.main"} align={"center"}>
            We've sent your plan!
          </Typography>
          <CheckCircleOutline
            color={"success"}
            fontSize={"large"}
            sx={{
              alignSelf: "center",
            }}
          />
          <Typography variant={"body2"} align={"center"}>
            Don't see your plan yet? Wait a couple minutes, check your spam
            folder, or resend.
          </Typography>
          {formFields}
          <Button variant="outlined" color="primary" type="submit">
            Resend
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={handleStartOver}
          >
            Start Over
          </Button>
        </Stack>
      )}
      {formStatus === FormStatus.Idle && (
        <Stack direction={"column"} spacing={3}>
          <Typography variant={"h6"} color={"primary.dark"}>
            Getting your plan
          </Typography>
          <Typography variant={"body1"}>
            Get your{" "}
            <strong>
              Emissions{" "}
              <Box component={"span"} color={green[600]}>
                Reduction
              </Box>{" "}
              Plan
            </strong>{" "}
            via email to save and share your results.
          </Typography>
          <Typography variant={"body1"} color={"primary.dark"}>
            Why email your plan?
          </Typography>
          <Typography variant={"body2"}>
            Over 60% of companies struggle to coordinate decarbonization efforts
            internally. Share the report with colleagues to align cross-team
            efforts.
          </Typography>
          <Typography variant={"body2"}>
            Save multiple scenarios to compare different pathways.
          </Typography>
          {formFields}
          <Button variant="contained" color="primary" type="submit">
            Send
          </Button>
        </Stack>
      )}
    </form>
  );
};

export default EmailPlanForm;
